import { useCallback, useState } from 'react';
import { Box, Button, Stack, TextField } from '@mui/material';

export interface AddNoteComposerProps {
  busy?: boolean;
  placeholder?: string;
  /** Rendered under the field. Omit where the surface says it elsewhere. */
  helperText?: string;
  onSave: (body: string) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}

/**
 * The note composer, one component for both places that take a note
 * (GDN-3380): the row menu, where it replaces the menu's own items, and the
 * workbench Notes section, where it sits under the list.
 *
 * Empty and whitespace-only saves are refused by disabling Save rather than by
 * failing after the click. A note is free text with no other validation; the
 * only wrong note is one that says nothing.
 */
export function AddNoteComposer({
  busy,
  placeholder = 'Add a note',
  helperText,
  onSave,
  onCancel,
  autoFocus,
}: AddNoteComposerProps) {
  const [body, setBody] = useState('');
  const trimmed = body.trim();

  const save = useCallback(() => {
    if (!trimmed) return;
    onSave(trimmed);
    setBody('');
  }, [onSave, trimmed]);

  return (
    <Box sx={{ px: 1.5, py: 1 }}>
      <TextField
        autoFocus={autoFocus}
        fullWidth
        multiline
        minRows={2}
        size="small"
        value={body}
        placeholder={placeholder}
        helperText={helperText}
        onChange={event => setBody(event.target.value)}
        onKeyDown={event => {
          /* Stopped before anything else: this composer renders INSIDE a MUI
             Menu, whose MenuList treats Arrow/Home/End on any descendant as
             list navigation and preventDefaults them — which would take the
             caret out of a multiline field mid-sentence. */
          event.stopPropagation();
          /* Same bargain as the archive dialog: Enter commits, Shift+Enter
             breaks the line, Escape hands the surface back unchanged. */
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (!busy) save();
          }
          if (event.key === 'Escape' && onCancel) onCancel();
        }}
        slotProps={{ htmlInput: { 'aria-label': placeholder } }}
        sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
      />
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 1 }}>
        {onCancel && (
          <Button color="inherit" onClick={onCancel} size="small" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        )}
        <Button
          disabled={busy || !trimmed}
          onClick={save}
          size="small"
          sx={{ textTransform: 'none' }}
          variant="contained"
        >
          Save
        </Button>
      </Stack>
    </Box>
  );
}
